import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { v1 as uuidv1 } from "uuid";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Currency formatting locale (matches `widget/src/lib/utils/locale.ts`).
 * Unlisted ISO 4217 codes use `DEFAULT_CURRENCY_FORMAT_LOCALE`.
 */
export const DEFAULT_CURRENCY_FORMAT_LOCALE = "en-US";

const CURRENCY_LOCALE_OVERRIDES: Record<string, string> = {
  AED: "ar-AE",
  ARS: "es-AR",
  AUD: "en-AU",
  BRL: "pt-BR",
  CAD: "en-CA",
  CHF: "de-CH",
  CLP: "es-CL",
  CNY: "zh-CN",
  COP: "es-CO",
  CZK: "cs-CZ",
  DKK: "da-DK",
  EGP: "ar-EG",
  EUR: "de-DE",
  GBP: "en-GB",
  HKD: "zh-HK",
  HUF: "hu-HU",
  IDR: "id-ID",
  ILS: "he-IL",
  INR: "en-IN",
  JPY: "ja-JP",
  KRW: "ko-KR",
  MXN: "es-MX",
  MYR: "ms-MY",
  NGN: "en-NG",
  NOK: "nb-NO",
  NZD: "en-NZ",
  PHP: "en-PH",
  PKR: "en-PK",
  PLN: "pl-PL",
  RON: "ro-RO",
  SAR: "ar-SA",
  SEK: "sv-SE",
  SGD: "en-SG",
  THB: "th-TH",
  TRY: "tr-TR",
  TWD: "zh-TW",
  USD: "en-US",
  VND: "vi-VN",
  ZAR: "en-ZA",
};

export function getLocaleForCurrency(currencyCode: string): string {
  const c = currencyCode.trim().toUpperCase();
  return CURRENCY_LOCALE_OVERRIDES[c] ?? DEFAULT_CURRENCY_FORMAT_LOCALE;
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate"
      ? (accurateSizes[i] ?? "Bytes")
      : (sizes[i] ?? "Bytes")
  }`;
}

// Generate a deterministic machine ID based on system information
function getSystemInfo(): string {
  if (typeof window === "undefined") return "";
  // Collect system information
  const info = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    screen.colorDepth,
    screen.width,
    screen.height,
  ].join("|");
  return info;
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

export function generateMachineId(): string {
  if (typeof window === "undefined") return "";

  // Get system information
  const systemInfo = getSystemInfo();

  // Generate a deterministic seed from system info
  const seed = hashString(systemInfo);

  // Create a deterministic timestamp based on the seed
  const timestamp =
    new Date(2024, 0, 1).getTime() + (seed % (365 * 24 * 60 * 60 * 1000));

  // Generate UUID v1 with deterministic values
  const uuid = uuidv1({
    msecs: timestamp,
    clockseq: seed % 0x3fff,
    node: new Uint8Array([0x00, 0x00, 0x00, 0x00, 0x00, seed % 0xff]),
  });

  return uuid;
}
